import React, { useState } from "react";
import Input from "@/components/forms/Input";
import Button from "@/components/button/Button";
import { Check, Pencil, X } from "lucide-react";

interface GenerationPeriodLabelProps {
    startDate?: string | null;
    endDate?: string | null;
    dateRange?: string;
    onDatesChange?: (startDate: string, endDate: string) => void;
}

const GenerationPeriodLabel: React.FC<GenerationPeriodLabelProps> = (
    {
        startDate,
        endDate,
        dateRange,
        onDatesChange,
    }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(startDate || "");
    const [tempEndDate, setTempEndDate] = useState(endDate || "");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const canEdit = !!onDatesChange;

    const handleEdit = () => {
        setTempStartDate(startDate || "");
        setTempEndDate(endDate || "");
        setErrorMessage(null);
        setIsEditing(true);
    };

    const handleConfirm = () => {
        if (!tempStartDate || !tempEndDate) {
            setErrorMessage("Veuillez sélectionner les dates de début et de fin de la période.");
            return;
        }

        if (new Date(tempStartDate) >= new Date(tempEndDate)) {
            setErrorMessage("La date de début doit être avant la date de fin.");
            return;
        }

        if (onDatesChange) {
            onDatesChange(tempStartDate, tempEndDate);
        }
        setErrorMessage(null);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setErrorMessage(null);
        setIsEditing(false);
    };

    return (
        <div className="text-center">
            <span className="text-xs uppercase text-gray-500 font-semibold tracking-wider">Période de génération</span>
            {isEditing ? (
                <div className="flex flex-col items-center gap-2 mt-2">
                    <div className="flex items-center justify-center gap-4">
                        <div className="flex items-center gap-2">
                            <label htmlFor="start-date" className="text-sm font-medium text-gray-700">Du</label>
                            <Input
                                id="start-date"
                                type="date"
                                value={tempStartDate}
                                onChange={(e) => {
                                    setTempStartDate(e.target.value);
                                    setErrorMessage(null);
                                }}
                                className="w-40"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label htmlFor="end-date" className="text-sm font-medium text-gray-700">au</label>
                            <Input
                                id="end-date"
                                type="date"
                                value={tempEndDate}
                                onChange={(e) => {
                                    setTempEndDate(e.target.value);
                                    setErrorMessage(null);
                                }}
                                className="w-40"
                            />
                        </div>
                        <div className="flex items-center gap-1">
                            <Button type="success" icon={<Check size={16}/>} onClick={handleConfirm}/>
                            <Button type="danger" icon={<X size={16}/>} onClick={handleCancel}/>
                        </div>
                    </div>
                    {errorMessage && (
                        <div className="text-sm text-red-600 font-medium">
                            {errorMessage}
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-center justify-center gap-2 mt-1">
                    <h6 className="text-2xl font-bold">{dateRange}</h6>
                    {canEdit && <Button type="primary" icon={<Pencil size={16}/>} onClick={handleEdit}/>}
                </div>
            )}
        </div>
    );
};

export default GenerationPeriodLabel;
